import Download from "@mui/icons-material/Download";
import { Link, Tooltip } from "@mui/material";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useAlerts } from "components/Alerts/AlertProvider";
import { downloadFromUrl } from "util/download";

interface S3FileLinkProps {
  /** S3 file key/path */
  fileKey: string;
}

/**
 * Displays a clickable link to download an S3 file
 * Shows the filename with a download icon, and displays the full S3 path in a tooltip
 * Handles the complete download flow including fetching signed URL and triggering download
 */
export function S3FileLink({ fileKey }: S3FileLinkProps) {
  const { fetchFile } = useCheckForFile();
  const alerts = useAlerts();

  const getFileName = (filePath: string): string => {
    const parts = filePath.split("/");
    return parts[parts.length - 1] || filePath;
  };

  const handleDownload = async () => {
    try {
      const signedUrl = await fetchFile(fileKey);
      if (signedUrl) {
        await downloadFromUrl(signedUrl, getFileName(fileKey));
      } else {
        throw new Error(`Failed to download file: ${fileKey}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        alerts.error(error.message);
      } else {
        alerts.error("An unknown error occurred while downloading file");
      }
    }
  };

  return (
    <Tooltip title={fileKey} placement="top-start">
      <Link
        component="button"
        variant="body2"
        onClick={handleDownload}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 0.5,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Download fontSize="small" />
        {getFileName(fileKey)}
      </Link>
    </Tooltip>
  );
}
